/** @jsx jsx */
import type { ReactNode } from 'react';
import { jsx, cssMap } from '@compiled/react';
import Link from '@jira/platform__link-with-analytics/src/LinkWithAnalytics.tsx';
import Avatar from '@atlaskit/avatar';
import { Box, Flex, xcss, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import DisabledBox from '../disabled-box/index.tsx';

type Props = {
	avatarUrl: string | undefined;
	displayName: string;
	href: string | undefined;
	appearance: 'square' | 'circle';
	isDeleting?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	onClick: () => void | null | undefined;
};

const styles = cssMap({
	avatarContainer: {
		paddingRight: token('space.100'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 0,
		flex: '0 0 auto',
		alignItems: 'center',
	},
	container: {
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		display: 'flex',
		flex: '1 1 auto',
	},
	contentContainer: {
		display: 'flex',
		alignItems: 'center',
		flex: '1 1 auto',
		width: '100%',
		overflow: 'hidden',
	},
	link: {
		width: '100%',
	},
	textContainer: {
		wordWrap: 'break-word',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
});

export default function AvatarTitleCell(props: Props): JSX.Element {
	const { avatarUrl, displayName, href, appearance, isDeleting, onClick } = props;

	const content = !href ? (
		<Flex alignItems="center" xcss={contentWrapperStyles}>
			<div css={styles.avatarContainer}>
				<Avatar src={avatarUrl} size="small" appearance={appearance} />
			</div>
			<Inline>
				{isDeleting && fg('hix-6302_project_delete_improvements') ? (
					<DisabledBox>{displayName}</DisabledBox>
				) : (
					displayName
				)}
			</Inline>
		</Flex>
	) : (
		<Content>
			<div css={styles.avatarContainer}>
				<Avatar src={avatarUrl} size="small" appearance={appearance} />
			</div>
			<div css={styles.textContainer}>
				{isDeleting && fg('hix-6302_project_delete_improvements') ? (
					<DisabledBox>{displayName} </DisabledBox>
				) : (
					displayName
				)}
			</div>
		</Content>
	);

	if (href) {
		return (
			<div css={styles.container}>
				{isDeleting && fg('hix-6302_project_delete_improvements') ? (
					content
				) : (
					<div css={styles.link}>
						<Box xcss={contentWrapperStyles}>
							<Tooltip
								content={displayName}
								isScreenReaderAnnouncementDisabled
								delay={1000}
								position="bottom-start"
							>
								<Link href={href} onClick={onClick}>
									{content}
								</Link>
							</Tooltip>
						</Box>
					</div>
				)}
			</div>
		);
	}
	return <div css={styles.contentContainer}>{content}</div>;
}

AvatarTitleCell.defaultProps = {
	avatarUrl: undefined,
	href: undefined,
	appearance: 'circle',
	onClick: undefined,
};

const Content = (props: { children: ReactNode }) => {
	return <Box xcss={accessibleContentContainerStyles}>{props.children}</Box>;
};

const contentWrapperStyles = xcss({ maxWidth: '100%', paddingBlock: 'space.075' });

const accessibleContentContainerStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	display: 'flex',
	alignItems: 'center',
	flex: '1 1 auto',
	width: '100%',
	height: '40px',
});
